import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import CryptoPayment from "@/components/CryptoPaymentReown";
import { 
  Send, 
  MessageCircle, 
  User, 
  Bot, 
  CheckCircle, 
  Phone, 
  MapPin, 
  Clock,
  Loader2,
  Home,
  Calendar,
  Mail,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Wallet
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Track current audio to prevent overlap
let currentAudio = null;

// Detect if accessed via QR code (lobby kiosk)
const getBookingSource = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // Also check hash params for HashRouter
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  return urlParams.get('source') || hashParams.get('source') || 'online';
};

export default function BookNow() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  // Persist session ID in sessionStorage to maintain conversation across re-renders
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('bitsy_session_id') || null;
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoListenEnabled, setAutoListenEnabled] = useState(true); // Auto-listen after Bitsy speaks
  const [pendingGreeting, setPendingGreeting] = useState(null); // For mobile - needs tap to play
  const [showCryptoPayment, setShowCryptoPayment] = useState(false);
  const [cryptoPaymentComplete, setCryptoPaymentComplete] = useState(false);
  const [pendingCryptoCheckout, setPendingCryptoCheckout] = useState(null);
  const [showIdCamera, setShowIdCamera] = useState(false);
  const [idVerifying, setIdVerifying] = useState(false);
  const [idVerificationResult, setIdVerificationResult] = useState(null);
  const [showSanctuaryAgreement, setShowSanctuaryAgreement] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const hasPlayedGreeting = useRef(false);
  const isRecordingRef = useRef(false);
  const userHasInteracted = useRef(false);
  const sessionIdRef = useRef(sessionId); // Track current session ID for async functions

  // Keep sessionIdRef in sync with sessionId state
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Handle successful crypto payment - uses MCP confirm endpoint
  const handleCryptoPaymentSuccess = async (paymentData) => {
    try {
      // Call MCP confirmation endpoint
      const confirmResponse = await axios.post(`${API}/mcp/confirm-crypto-payment`, {
        reservation_id: pendingCryptoCheckout?.reservationId,
        tx_hash: paymentData.txHash,
        network: paymentData.network,
        amount_paid: paymentData.amount,
        currency: paymentData.token
      });
      
      setCryptoPaymentComplete(true);
      setShowCryptoPayment(false);
      
      // Add the "You're officially a Hodler!" confirmation message to chat
      if (confirmResponse.data.confirmation_message) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: confirmResponse.data.confirmation_message
        }]);
        
        // Speak the confirmation
        if (voiceEnabled) {
          speakText("Transaction confirmed! You're officially a Hodler. Your retreat at Hodler Inn is secured. Check your email for details.");
        }
      } else {
        // Fallback message
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `🎉 Payment received! ${paymentData.amount} ${paymentData.token} on ${paymentData.network}. Your booking is now confirmed and paid with the 10% Hodler Discount! Transaction: ${paymentData.txHash.slice(0, 16)}...`
        }]);
      }
      
      // Clear pending checkout
      setPendingCryptoCheckout(null);
      
    } catch (error) {
      console.error('Failed to confirm crypto payment:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Payment received! Transaction: ${paymentData.txHash.slice(0, 16)}... However, I had trouble confirming with our system. Please call (918) 653-7801 with your transaction hash for verification.`
      }]);
    }
  };

  // Handle crypto payment cancellation
  const handleCryptoPaymentCancel = async () => {
    if (pendingCryptoCheckout?.reservationId) {
      try {
        await axios.post(`${API}/mcp/cancel-crypto-checkout?reservation_id=${pendingCryptoCheckout.reservationId}`);
      } catch (error) {
        console.error('Failed to cancel crypto checkout:', error);
      }
    }
    
    setShowCryptoPayment(false);
    setPendingCryptoCheckout(null);
    
    // Let Bitsy know the user cancelled
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: "No problem! Would you like to proceed with a standard checkout instead, or would you like to try the crypto payment again?"
    }]);
  };

  // Detect if mobile device
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Speak text using Web Speech API
  const speakText = (text, isGreeting = false) => {
    if (!voiceEnabled || !('speechSynthesis' in window)) return;
    
    // On mobile, if user hasn't interacted yet and this is the greeting, save it for later
    if (isMobile() && isGreeting && !userHasInteracted.current) {
      setPendingGreeting(text);
      return;
    }
    
    // Stop any currently playing
    stopSpeaking();
    
    // Clean text for speech
    const cleanText = text.replace(/[🏨⚠️•\n\*]/g, ' ').replace(/\$/g, ' dollars ').replace(/\s+/g, ' ').trim();
    
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 0.9;
    
    // Try to get a good voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.toLowerCase().includes('samantha') ||
      v.name.toLowerCase().includes('google us english') ||
      v.name.toLowerCase().includes('microsoft zira') ||
      v.lang === 'en-US'
    );
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      // Auto-start listening after Bitsy finishes speaking (if enabled and not already recording)
      if (autoListenEnabled && voiceEnabled && !isRecordingRef.current && !bookingConfirmed) {
        setTimeout(() => {
          if (!isRecordingRef.current && !isSpeaking) {
            startRecording();
          }
        }, 500); // Small delay before auto-listen
      }
    };
    utterance.onerror = (e) => {
      console.log('Speech error:', e);
      setIsSpeaking(false);
    };
    
    // On mobile, resume speechSynthesis in case it's paused
    if (isMobile()) {
      window.speechSynthesis.cancel(); // Clear any stuck utterances
      setTimeout(() => {
        window.speechSynthesis.speak(utterance);
      }, 100);
    } else {
      window.speechSynthesis.speak(utterance);
    }
  };

  // Stop speaking
  const stopSpeaking = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio = null;
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  // Fetch availability and play voice greeting on load
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const res = await axios.get(`${API}/chatbot/availability`);
        setAvailability(res.data);
        
        // Check if we have an existing session with history
        const existingSessionId = sessionStorage.getItem('bitsy_session_id');
        if (existingSessionId) {
          try {
            const historyRes = await axios.get(`${API}/chatbot/history/${existingSessionId}`);
            if (historyRes.data.messages && historyRes.data.messages.length > 0) {
              // Restore existing conversation
              setMessages(historyRes.data.messages.map(m => ({
                role: m.role,
                content: m.content
              })));
              setSessionId(existingSessionId);
              hasPlayedGreeting.current = true; // Don't replay greeting
              return; // Don't show welcome message
            }
          } catch (e) {
            // Session expired or not found, clear it
            sessionStorage.removeItem('bitsy_session_id');
          }
        }
        
        // Get pricing info
        const singleRate = res.data.single_rate || 79;
        const doubleRate = res.data.double_rate || 89;
        const taxRate = res.data.tax_rate || 0;
        
        // Simple greeting without rate info (rates shown in card below)
        let welcomeMsg = `Hi, I'm Bitsy! I'm here to help you find the perfect retreat at Hodler Inn. How can I assist you today?`;
        
        if (res.data.is_sold_out) {
          welcomeMsg = `Hi, I'm Bitsy! I'm here to help you find the perfect retreat at Hodler Inn. We're currently fully booked for tonight, but I'd be happy to help with a future reservation or answer any questions!`;
        }
        
        setMessages([{ role: "assistant", content: welcomeMsg }]);
        
        // Play voice greeting using Web Speech API (autoplays on desktop, needs tap on mobile)
        if (!hasPlayedGreeting.current) {
          hasPlayedGreeting.current = true;
          setTimeout(() => {
            const greeting = res.data.is_sold_out 
              ? "Hi, I'm Bitsy! I'm here to help you find the perfect retreat at Hodler Inn. We're fully booked for tonight, but I'd be happy to help with a future reservation!"
              : "Hi, I'm Bitsy! I'm here to help you find the perfect retreat at Hodler Inn. How can I assist you today?";
            speakText(greeting, true); // true = isGreeting
          }, 500);
        }
      } catch (error) {
        const defaultMsg = "Hi, I'm Bitsy! I'm here to help you find the perfect retreat at Hodler Inn. How can I assist you today?";
        setMessages([{ role: "assistant", content: defaultMsg }]);
        
        // Play default greeting
        if (!hasPlayedGreeting.current) {
          hasPlayedGreeting.current = true;
          setTimeout(() => {
            speakText("Hi, I'm Bitsy! I'm here to help you find the perfect retreat at Hodler Inn. How can I assist you today?", true);
          }, 500);
        }
      }
    };
    
    // Load voices first (they may not be available immediately)
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
    
    fetchAvailability();
    
    // Cleanup on unmount
    return () => {
      stopSpeaking();
    };
  }, []);

  // Save sessionId to sessionStorage when it changes
  useEffect(() => {
    if (sessionId) {
      sessionStorage.setItem('bitsy_session_id', sessionId);
      console.log('[Bitsy] Session ID saved:', sessionId);
    }
  }, [sessionId]);

  // Handle first user interaction on mobile - play pending greeting
  const handleUserInteraction = () => {
    if (!userHasInteracted.current) {
      userHasInteracted.current = true;
      if (pendingGreeting) {
        speakText(pendingGreeting, false); // Now safe to play
        setPendingGreeting(null);
      }
    }
  };

  // Play pending greeting when user taps the button
  const playPendingGreeting = () => {
    userHasInteracted.current = true;
    if (pendingGreeting) {
      speakText(pendingGreeting, false);
      setPendingGreeting(null);
    }
  };

  // Auto scroll to bottom - works with Shadcn ScrollArea
  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollRef.current) {
        // Shadcn ScrollArea has a viewport inside it
        const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
        } else {
          scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
        }
      }
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Use agentic Bitsy (function calling) vs legacy (prompt-based)
  const USE_AGENTIC_BITSY = true;
  
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    // Mark user as having interacted
    handleUserInteraction();

    const userMessage = inputValue.trim();
    setInputValue("");
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      // Choose endpoint based on agentic mode
      const endpoint = USE_AGENTIC_BITSY ? `${API}/chatbot/agentic` : `${API}/chatbot/message`;
      
      const response = await axios.post(endpoint, {
        message: userMessage,
        session_id: sessionId,
        source: getBookingSource() // "online" or "qr_walkin"
      });

      console.log('[Bitsy] Message sent with session:', sessionId, '-> Got session:', response.data.session_id, 'Agentic:', response.data.is_agentic);
      
      // Update both state and ref immediately to ensure next message uses correct session
      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      sessionIdRef.current = newSessionId; // Update ref immediately for voice flow
      
      // Add assistant response
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response 
      }]);

      // Speak the response
      if (voiceEnabled) {
        speakText(response.data.response);
      }
      
      // Check if Bitsy is asking for ID verification (walk-in flow)
      const responseText = response.data.response.toLowerCase();
      if (responseText.includes('verify your id') || 
          responseText.includes('hold your driver') || 
          responseText.includes('hold your id') ||
          responseText.includes('show your id')) {
        // Show camera for ID verification
        setShowIdCamera(true);
      }
      
      // Check if Bitsy is showing the Sanctuary Agreement
      if (responseText.includes('sanctuary guidelines') || 
          responseText.includes('bed purity') ||
          responseText.includes('do you agree to these guidelines')) {
        // Show Sanctuary Agreement modal
        setShowSanctuaryAgreement(true);
      }

      // Check if booking was created (works for both agentic and legacy)
      if (response.data.booking_created && response.data.booking_details) {
        setBookingConfirmed(response.data.booking_details);
        console.log('[Bitsy] Booking confirmed:', response.data.booking_details);
      }

      // Check for MCP tool call (crypto checkout) - legacy format
      if (response.data.tool_call && response.data.tool_call.tool === 'trigger_crypto_checkout') {
        const toolArgs = response.data.tool_call.args;
        
        // Store the crypto checkout data and show payment modal
        setPendingCryptoCheckout({
          reservationId: toolArgs.reservation_id,
          amount: toolArgs.crypto_amount_usd,
          originalAmount: toolArgs.original_amount_usd,
          guestName: toolArgs.guest_name,
          guestEmail: toolArgs.guest_email,
          checkIn: toolArgs.check_in,
          checkOut: toolArgs.check_out,
          roomType: toolArgs.room_type,
          currency: toolArgs.preferred_currency
        });
        
        // Show crypto payment modal
        setShowCryptoPayment(true);
        
        console.log('[BITSY] Crypto checkout triggered:', toolArgs);
      }

    } catch (error) {
      console.error("Chat error:", error);
      const errorMsg = "I apologize, but I'm having trouble processing your request. Please try again or call us directly at (918) 653-7801.";
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: errorMsg 
      }]);
      if (voiceEnabled) {
        speakText(errorMsg);
      }
    }

    setIsLoading(false);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Voice recording with automatic silence detection
  const silenceTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      // Stop any currently playing speech
      stopSpeaking();
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      // Set up audio analysis for silence detection
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 512;
      
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      let silenceStart = null;
      const SILENCE_THRESHOLD = 15; // Audio level below this is considered silence
      const SILENCE_DURATION = 1500; // Stop after 1.5 seconds of silence
      
      // Monitor for silence
      const checkSilence = () => {
        if (!isRecordingRef.current || !analyserRef.current) return;
        
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
        
        if (average < SILENCE_THRESHOLD) {
          if (!silenceStart) {
            silenceStart = Date.now();
          } else if (Date.now() - silenceStart > SILENCE_DURATION) {
            // Silence detected for long enough, stop recording
            stopRecording();
            return;
          }
        } else {
          silenceStart = null; // Reset if sound detected
        }
        
        silenceTimeoutRef.current = requestAnimationFrame(checkSilence);
      };

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        // Clean up silence detection
        if (silenceTimeoutRef.current) {
          cancelAnimationFrame(silenceTimeoutRef.current);
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      isRecordingRef.current = true;
      
      // Start silence detection after a brief delay (to allow user to start speaking)
      setTimeout(() => {
        checkSilence();
      }, 1000);
      
    } catch (error) {
      console.error("Microphone access denied:", error);
      alert("Please allow microphone access to use voice input.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecordingRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      isRecordingRef.current = false;
      
      // Clean up
      if (silenceTimeoutRef.current) {
        cancelAnimationFrame(silenceTimeoutRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const response = await axios.post(`${API}/chatbot/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.transcript) {
        setInputValue(response.data.transcript);
        // Auto-send the transcribed message
        const userMessage = response.data.transcript;
        setInputValue("");
        setMessages(prev => [...prev, { role: "user", content: userMessage }]);
        
        // Send to chatbot - use ref to get latest session ID
        const currentSessionId = sessionIdRef.current;
        console.log('[Bitsy] Voice: Sending with session:', currentSessionId);
        const chatResponse = await axios.post(`${API}/chatbot/message`, {
          message: userMessage,
          session_id: currentSessionId,
          source: getBookingSource() // "online" or "qr_walkin"
        });
        
        console.log('[Bitsy] Voice: Got session:', chatResponse.data.session_id);
        
        // Update both state and ref immediately
        const newSessionId = chatResponse.data.session_id;
        setSessionId(newSessionId);
        sessionIdRef.current = newSessionId;
        
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: chatResponse.data.response 
        }]);
        
        // Speak the response and auto-listen will trigger after
        if (voiceEnabled) {
          speakText(chatResponse.data.response);
        }
        
        if (chatResponse.data.booking_created && chatResponse.data.booking_details) {
          setBookingConfirmed(chatResponse.data.booking_details);
        }
      }
    } catch (error) {
      console.error("Transcription error:", error);
      const errorMsg = "I couldn't understand that. Please try again or type your message.";
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: errorMsg 
      }]);
      if (voiceEnabled) {
        speakText(errorMsg);
      }
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-black/40 backdrop-blur-md border-b border-amber-500/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a 
            href="/" 
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
              <Home className="w-5 h-5 text-black" />
            </div>
            <div>
              <h1 className="font-bold text-xl text-white">Hodler Inn</h1>
              <p className="text-xs text-amber-400/80">Heavener, Oklahoma</p>
            </div>
          </a>
          <a 
            href="tel:9186537801" 
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 rounded-full font-semibold transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">(918) 653-7801</span>
          </a>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-black/30 backdrop-blur border border-amber-500/20 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
              <MapPin className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-white font-medium text-sm">Location</p>
              <p className="text-gray-400 text-xs">820 US-59, Heavener</p>
            </div>
          </div>
          <div className="bg-black/30 backdrop-blur border border-amber-500/20 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-white font-medium text-sm">Check-in / Out</p>
              <p className="text-gray-400 text-xs">3:00 PM / 11:00 AM</p>
            </div>
          </div>
          <div className="bg-black/30 backdrop-blur border border-amber-500/20 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-white font-medium text-sm">Rates From</p>
              <p className="text-gray-400 text-xs">$79 + tax</p>
            </div>
          </div>
        </div>

        {/* Chat Container */}
        <div className="bg-black/40 backdrop-blur border border-amber-500/20 rounded-2xl overflow-hidden shadow-2xl">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-amber-500/20 to-amber-600/10 border-b border-amber-500/20 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Chat with Bitsy</h2>
                <p className="text-amber-400/80 text-sm">Your AI booking assistant</p>
              </div>
            </div>
          </div>

          {/* Booking Confirmation Banner */}
          <AnimatePresence>
            {bookingConfirmed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="bg-emerald-500/20 border-b border-emerald-500/30 px-6 py-4"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-emerald-400 font-semibold">
                      {cryptoPaymentComplete ? '✓ Booking Confirmed & Paid!' : 'Reservation Request Received!'}
                    </p>
                    <p className="text-gray-300 text-sm mt-1">
                      Confirmation sent to {bookingConfirmed.email}
                    </p>
                    <div className="mt-2 bg-black/30 rounded-lg p-3 text-sm">
                      <p className="text-white">{bookingConfirmed.guest_name}</p>
                      <p className="text-gray-400">{bookingConfirmed.check_in} → {bookingConfirmed.check_out}</p>
                      <p className="text-amber-400">${bookingConfirmed.total} total ({bookingConfirmed.nights} nights)</p>
                    </div>
                    
                    {/* Payment Options */}
                    {!cryptoPaymentComplete && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2 text-amber-400 text-sm">
                          <Phone className="w-4 h-4" />
                          <span>Call (918) 653-7801 to confirm</span>
                        </div>
                        <div className="text-gray-400 text-xs">— OR —</div>
                        <button
                          onClick={() => setShowCryptoPayment(true)}
                          className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black px-4 py-2 rounded-lg text-sm font-bold transition-all w-full justify-center"
                          data-testid="pay-crypto-btn"
                        >
                          <Wallet className="w-4 h-4" />
                          Pay with Crypto (USDC/USDT/BTC)
                        </button>
                      </div>
                    )}
                    
                    {cryptoPaymentComplete && (
                      <div className="mt-3 flex items-center gap-2 text-green-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span>Payment confirmed - no call needed!</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Messages Area */}
          <ScrollArea className="h-[400px] p-4" ref={scrollRef}>
            <div className="space-y-4">
              {/* Tap to hear greeting button for mobile */}
              {pendingGreeting && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex justify-center"
                >
                  <button
                    onClick={playPendingGreeting}
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black px-4 py-2 rounded-full text-sm font-medium hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg"
                  >
                    <Volume2 className="w-4 h-4" />
                    <span>Tap to hear Bitsy</span>
                  </button>
                </motion.div>
              )}
              
              <AnimatePresence mode="popLayout">
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-amber-400" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                        msg.role === "user"
                          ? "bg-amber-500 text-black rounded-br-md"
                          : "bg-slate-800/80 text-gray-200 rounded-bl-md border border-slate-700/50"
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                    </div>
                    {msg.role === "user" && (
                      <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-gray-300" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Rate Card - shown ONLY once after the initial greeting */}
              {messages.length === 1 && messages[0].role === "assistant" && availability && !availability.is_sold_out && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex gap-3"
                >
                  <div className="w-8 h-8 flex-shrink-0"></div>
                  <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-amber-500/30 rounded-xl p-4 max-w-[90%]">
                    <h4 className="text-amber-400 font-semibold text-center text-sm mb-3">Room Rates</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-black/40 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-xs">Single Bed</p>
                        <p className="text-white font-bold">${availability.single_rate || 79}</p>
                        {(availability.tax_rate || 0) > 0 && (
                          <>
                            <p className="text-gray-500 text-xs">+ ${((availability.single_rate || 79) * (availability.tax_rate / 100)).toFixed(2)} tax</p>
                            <p className="text-amber-400 font-semibold text-xs">
                              Total: ${((availability.single_rate || 79) * (1 + availability.tax_rate / 100)).toFixed(2)}/night
                            </p>
                          </>
                        )}
                      </div>
                      <div className="bg-black/40 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-xs">Double Bed</p>
                        <p className="text-white font-bold">${availability.double_rate || 89}</p>
                        {(availability.tax_rate || 0) > 0 && (
                          <>
                            <p className="text-gray-500 text-xs">+ ${((availability.double_rate || 89) * (availability.tax_rate / 100)).toFixed(2)} tax</p>
                            <p className="text-amber-400 font-semibold text-xs">
                              Total: ${((availability.double_rate || 89) * (1 + availability.tax_rate / 100)).toFixed(2)}/night
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-700/50 flex justify-center gap-3 text-xs text-gray-500">
                      <span>Check-in: 3 PM</span>
                      <span>•</span>
                      <span>Check-out: 11 AM</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Loading indicator */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-3"
                >
                  <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <Bot className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="bg-slate-800/80 px-4 py-3 rounded-2xl rounded-bl-md border border-slate-700/50">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                      <span className="text-gray-400 text-sm">Typing...</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="border-t border-amber-500/20 p-4 bg-black/20">
            {/* Regular Chat Input - always show */}
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (isSpeaking) {
                    stopSpeaking();
                  }
                  setVoiceEnabled(!voiceEnabled);
                }}
                className={`px-3 transition-all ${
                  voiceEnabled 
                    ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30" 
                    : "bg-slate-800 hover:bg-slate-700 text-gray-500"
                }`}
                data-testid="voice-toggle-btn"
                title={voiceEnabled ? "Voice on - click to mute" : "Voice off - click to enable"}
              >
                {voiceEnabled ? (
                  isSpeaking ? <Volume2 className="w-4 h-4 animate-pulse" /> : <Volume2 className="w-4 h-4" />
                ) : (
                  <VolumeX className="w-4 h-4" />
                )}
              </Button>
              {/* Auto-listen toggle */}
              <Button
                onClick={() => setAutoListenEnabled(!autoListenEnabled)}
                className={`px-3 transition-all ${
                  autoListenEnabled 
                    ? "bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30" 
                    : "bg-slate-800 hover:bg-slate-700 text-gray-500"
                }`}
                data-testid="auto-listen-toggle-btn"
                title={autoListenEnabled ? "Auto-listen ON - mic activates after Bitsy speaks" : "Auto-listen OFF - click mic manually"}
              >
                <span className="text-xs font-medium">{autoListenEnabled ? "AUTO" : "MAN"}</span>
              </Button>
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isRecording ? "Listening..." : "Type your message or tap mic to speak..."}
                className="flex-1 bg-slate-800/80 border-slate-700 text-white placeholder:text-gray-500 focus:border-amber-500/50 focus:ring-amber-500/20"
                disabled={isLoading || isRecording}
                data-testid="chat-input"
              />
              <Button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isLoading}
                className={`px-4 transition-all ${
                  isRecording 
                    ? "bg-red-500 hover:bg-red-400 text-white animate-pulse" 
                    : "bg-slate-700 hover:bg-slate-600 text-gray-300"
                }`}
                data-testid="voice-btn"
                title={isRecording ? "Stop recording" : "Start voice input"}
              >
                {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="bg-amber-500 hover:bg-amber-400 text-black px-6"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-center text-gray-500 text-xs mt-3">
              By making a reservation, you agree to confirm by calling (918) 653-7801
            </p>
            
            {/* Policy Notice - visible in chat */}
            <div className="flex items-center justify-center gap-4 mt-3 py-2 px-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                </svg>
                <span className="text-red-400 text-xs font-bold">100% Non-Smoking</span>
              </div>
              <div className="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                </svg>
                <span className="text-red-400 text-xs font-bold">No Pets</span>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="mt-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-semibold text-sm">Important Notice</p>
              <p className="text-gray-400 text-sm mt-1">
                Online reservations must be confirmed by calling (918) 653-7801. 
                Unconfirmed reservations will be automatically cancelled 48 hours before arrival.
                Payment is collected at check-in.
              </p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-6 text-center">
          <a href="tel:9186537801" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors">
            <Phone className="w-4 h-4" />
            <span>(918) 653-7801</span>
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-8 py-6 bg-slate-900/50 border-t border-slate-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-amber-500/60" />
              <span className="text-gray-500 text-sm">820 US-59, Heavener, OK 74937</span>
            </div>
            <span className="text-gray-600 hidden md:inline">•</span>
            <p className="text-gray-600 text-xs">
              © {new Date().getFullYear()} Hodler Inn
            </p>
          </div>
        </div>
      </footer>

      {/* Crypto Payment Modal - supports both standard and Bitsy MCP flows */}
      {showCryptoPayment && (pendingCryptoCheckout || bookingConfirmed) && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <CryptoPayment
              amount={pendingCryptoCheckout?.amount || bookingConfirmed?.total}
              bookingDetails={{
                nights: bookingConfirmed?.nights || 1,
                roomType: pendingCryptoCheckout?.roomType || 'Standard Room',
                guestName: pendingCryptoCheckout?.guestName || bookingConfirmed?.guest_name,
                checkIn: pendingCryptoCheckout?.checkIn,
                checkOut: pendingCryptoCheckout?.checkOut,
                reservationId: pendingCryptoCheckout?.reservationId,
                isHodlerDiscount: !!pendingCryptoCheckout // Shows 10% discount badge
              }}
              onSuccess={handleCryptoPaymentSuccess}
              onCancel={handleCryptoPaymentCancel}
            />
          </motion.div>
        </div>
      )}

      {/* ID Verification Camera Modal (Walk-in Flow) */}
      {showIdCamera && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-vault-surface border border-vault-gold/30 rounded-2xl p-6 max-w-lg w-full"
          >
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-vault-gold mb-2">ID Verification</h3>
              <p className="text-vault-text-secondary text-sm">
                Hold your ID up to the camera and tap <strong>Scan ID</strong>. We extract your info but <strong>never store</strong> the image.
              </p>
            </div>
            
            <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '4/3' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute inset-0 border-2 border-dashed border-vault-gold/50 m-8 rounded-lg pointer-events-none" />
              <div className="absolute bottom-2 left-2 right-2 text-center">
                <span className="bg-black/70 text-white text-xs px-2 py-1 rounded">
                  Position ID within the frame
                </span>
              </div>
              {idVerifying && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-vault-gold animate-spin mx-auto mb-2" />
                    <p className="text-white">Scanning ID...</p>
                  </div>
                </div>
              )}
            </div>
            
            {idVerificationResult && (
              <div className={`mb-4 p-3 rounded-lg ${idVerificationResult.is_valid_id ? 'bg-emerald-900/50 border border-emerald-500' : 'bg-red-900/50 border border-red-500'}`}>
                {idVerificationResult.is_valid_id ? (
                  <div className="text-emerald-300 text-sm">
                    <p className="font-semibold">✅ Valid {idVerificationResult.id_type?.replace('_', ' ')}</p>
                    <p>Name: {idVerificationResult.full_name}</p>
                    {idVerificationResult.address && <p className="text-xs">Address: {idVerificationResult.address}</p>}
                    <p>Age 18+: {idVerificationResult.age_verified ? '✓ Verified' : '✗ Not verified'}</p>
                  </div>
                ) : (
                  <div className="text-red-300 text-sm">
                    <p className="font-semibold">❌ Invalid ID</p>
                    <p>{idVerificationResult.error_message || 'Please show a valid government-issued ID'}</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  // Start camera
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ 
                      video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } 
                    });
                    if (videoRef.current) {
                      videoRef.current.srcObject = stream;
                    }
                    setIdVerificationResult(null);
                  } catch (err) {
                    console.error('Camera access error:', err);
                    toast.error('Could not access camera. Please allow camera permissions.');
                  }
                }}
                className="flex-1 bg-vault-gold text-black font-semibold py-3 rounded-lg hover:bg-vault-gold/90"
                disabled={idVerifying}
              >
                Start Camera
              </button>
              <button
                onClick={async () => {
                  if (!videoRef.current?.srcObject) {
                    toast.error('Please start the camera first');
                    return;
                  }
                  
                  setIdVerifying(true);
                  setIdVerificationResult(null);
                  
                  try {
                    // Capture frame from video
                    const video = videoRef.current;
                    const canvas = canvasRef.current;
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0);
                    
                    // Convert to base64 (JPEG for smaller size)
                    const imageBase64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
                    
                    // Send to verification API
                    const response = await axios.post(`${API}/verify-id`, {
                      image_base64: imageBase64
                    });
                    
                    setIdVerificationResult(response.data);
                    
                    if (response.data.is_valid_id && response.data.age_verified) {
                      toast.success('ID verified successfully!');
                      // Auto-fill name if we got it
                      if (response.data.full_name) {
                        // Store for later use in booking
                        sessionStorage.setItem('verified_name', response.data.full_name);
                        sessionStorage.setItem('verified_address', response.data.address || '');
                      }
                    } else if (response.data.is_valid_id && !response.data.age_verified) {
                      toast.error('Guest must be 18 or older');
                    } else {
                      toast.error('Please show a valid government ID');
                    }
                  } catch (err) {
                    console.error('ID verification error:', err);
                    toast.error('Failed to verify ID. Please try again.');
                    setIdVerificationResult({
                      is_valid_id: false,
                      error_message: 'Verification failed. Please try again.'
                    });
                  } finally {
                    setIdVerifying(false);
                  }
                }}
                className="flex-1 bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-500 flex items-center justify-center gap-2"
                disabled={idVerifying}
              >
                {idVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Scan ID
              </button>
            </div>
            
            {idVerificationResult?.is_valid_id && idVerificationResult?.age_verified && (
              <button
                onClick={() => {
                  // Stop camera and close modal
                  if (videoRef.current?.srcObject) {
                    videoRef.current.srcObject.getTracks().forEach(track => track.stop());
                  }
                  setShowIdCamera(false);
                  
                  // Send message to Bitsy with verified name
                  const verifiedName = idVerificationResult.full_name || 'Guest';
                  setMessages(prev => [...prev, { 
                    role: "user", 
                    content: `My ID has been verified. My name is ${verifiedName}.` 
                  }]);
                  
                  // Auto-send to continue conversation
                  setTimeout(async () => {
                    try {
                      const response = await axios.post(`${API}/chatbot/message`, {
                        message: `ID verified. Guest name: ${verifiedName}. Address: ${idVerificationResult.address || 'Not provided'}. Age verified: Yes.`,
                        session_id: sessionIdRef.current,
                        source: getBookingSource()
                      });
                      
                      const newSessionId = response.data.session_id;
                      setSessionId(newSessionId);
                      sessionIdRef.current = newSessionId;
                      
                      setMessages(prev => [...prev, { 
                        role: "assistant", 
                        content: response.data.response 
                      }]);
                      
                      if (voiceEnabled) {
                        speakText(response.data.response);
                      }
                    } catch (err) {
                      console.error('Failed to continue after ID verification:', err);
                    }
                  }, 500);
                  
                  setIdVerificationResult(null);
                }}
                className="w-full mt-3 bg-emerald-600 text-white font-semibold py-3 rounded-lg hover:bg-emerald-500"
              >
                ✓ Continue with Booking
              </button>
            )}
            
            <button
              onClick={() => {
                if (videoRef.current?.srcObject) {
                  videoRef.current.srcObject.getTracks().forEach(track => track.stop());
                }
                setShowIdCamera(false);
                setIdVerificationResult(null);
              }}
              className="w-full mt-3 text-vault-text-secondary hover:text-white py-2"
            >
              Cancel
            </button>
          </motion.div>
        </div>
      )}

      {/* Sanctuary Agreement Modal (Walk-in Flow) */}
      {showSanctuaryAgreement && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-vault-surface border border-vault-gold/30 rounded-2xl p-6 max-w-lg w-full"
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-vault-gold mb-2">🛡️ Sanctuary Guidelines</h3>
              <p className="text-vault-text-secondary text-sm">
                Please acknowledge our house rules before check-in
              </p>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="bg-vault-surface-highlight rounded-lg p-4 border border-vault-border">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🛏️</span>
                  <div>
                    <h4 className="font-semibold text-vault-gold">BED PURITY</h4>
                    <p className="text-vault-text-secondary text-sm">
                      Please keep all footwear and food off our fresh white quilts.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-vault-surface-highlight rounded-lg p-4 border border-vault-border">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🌬️</span>
                  <div>
                    <h4 className="font-semibold text-vault-gold">CLEAN AIR</h4>
                    <p className="text-vault-text-secondary text-sm">
                      We are a 100% smoke-free and pet-free property.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-vault-surface-highlight rounded-lg p-4 border border-vault-border">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📱</span>
                  <div>
                    <h4 className="font-semibold text-vault-gold">CLEAR SPACE</h4>
                    <p className="text-vault-text-secondary text-sm">
                      Nightstands are kept clear for your personal items or medical devices.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => {
                setShowSanctuaryAgreement(false);
                // Send agreement to Bitsy
                setInputValue("I agree to the sanctuary guidelines");
                // Auto-submit after a short delay
                setTimeout(() => {
                  const submitBtn = document.querySelector('[data-testid="chat-send-btn"]');
                  if (submitBtn) submitBtn.click();
                }, 300);
              }}
              className="w-full bg-emerald-600 text-white font-bold py-4 rounded-lg hover:bg-emerald-500 transition-colors text-lg"
              data-testid="sanctuary-agree-btn"
            >
              ✓ I AGREE - CHECK ME IN
            </button>
            
            <button
              onClick={() => {
                setShowSanctuaryAgreement(false);
                setMessages(prev => [...prev, { 
                  role: "user", 
                  content: "I need to speak with the front desk about the guidelines" 
                }]);
              }}
              className="w-full mt-3 text-vault-text-secondary hover:text-white py-2 text-sm"
            >
              I have questions about the guidelines
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
